import { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

const OutletContext = createContext({});

export const useOutlet = () => {
  const context = useContext(OutletContext);
  if (!context) {
    throw new Error('useOutlet must be used within an OutletProvider');
  }
  return context;
};

export const OutletProvider = ({ children, merchantData }) => {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  
  const [businesses, setBusinesses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [outlets, setOutlets] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch businesses for the merchant
  const fetchBusinesses = async () => {
    if (!merchantData?.id) return;

    setLoading(true);
    setError(null);
    try {
      console.log('[MDM] Fetching businesses for merchant ID:', merchantData.id);
      console.log('[MDM] Merchant login ID:', merchantData.merchantId);
      
      const q = query(
        collection(db, 'businesses'),
        where('merchantId', '==', merchantData.id)
      );
      
      const snapshot = await getDocs(q);
      const businessList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('[MDM] Found businesses:', businessList.length);
      setBusinesses(businessList);
    } catch (err) {
      console.error('[MDM] Error fetching businesses:', err);
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  // Fetch countries for selected business
  const fetchCountries = async (businessId) => {
    if (!businessId) return;

    setLoading(true);
    setError(null);
    try {
      console.log('[MDM] Fetching countries for business:', businessId);
      
      const q = query(
        collection(db, 'countries'),
        where('businessId', '==', businessId)
      );
      
      const snapshot = await getDocs(q);
      const countryList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('[MDM] Found countries:', countryList.length);
      setCountries(countryList);
    } catch (err) {
      console.error('[MDM] Error fetching countries:', err);
      setError('Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  // Fetch states for selected country
  const fetchStates = async (stateIds) => {
    if (!stateIds || stateIds.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      console.log('[MDM] Fetching states:', stateIds);
      
      // Fetch all states by their IDs
      const statePromises = stateIds.map(id => 
        getDoc(doc(db, 'states', id))
      );
      
      const stateDocs = await Promise.all(statePromises);
      const stateList = stateDocs
        .filter(doc => doc.exists())
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      
      console.log('[MDM] Found states:', stateList.length);
      setStates(stateList);
    } catch (err) {
      console.error('[MDM] Error fetching states:', err);
      setError('Failed to load states');
    } finally {
      setLoading(false);
    }
  };

  // Fetch cities for selected state
  const fetchCities = async (cityIds) => {
    if (!cityIds || cityIds.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      console.log('[MDM] Fetching cities:', cityIds);
      
      // Fetch all cities by their IDs
      const cityPromises = cityIds.map(id => 
        getDoc(doc(db, 'cities', id))
      );
      
      const cityDocs = await Promise.all(cityPromises);
      const cityList = cityDocs
        .filter(doc => doc.exists())
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      
      console.log('[MDM] Found cities:', cityList.length);
      setCities(cityList);
    } catch (err) {
      console.error('[MDM] Error fetching cities:', err);
      setError('Failed to load cities');
    } finally {
      setLoading(false);
    }
  };

  // Fetch outlets for selected city
  const fetchOutlets = async (outletIds) => {
    if (!outletIds || outletIds.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      console.log('[MDM] Fetching outlets:', outletIds);
      
      // Fetch all outlets by their IDs
      const outletPromises = outletIds.map(id => 
        getDoc(doc(db, 'outlets', id))
      );
      
      const outletDocs = await Promise.all(outletPromises);
      const outletList = outletDocs
        .filter(doc => doc.exists())
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      
      console.log('[MDM] Found outlets:', outletList.length);
      setOutlets(outletList);
    } catch (err) {
      console.error('[MDM] Error fetching outlets:', err);
      setError('Failed to load outlets');
    } finally {
      setLoading(false);
    }
  };

  // Handle business selection
  const handleSelectBusiness = async (business) => {
    console.log('[MDM] Business selected:', business.name);
    setSelectedBusiness(business);
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedOutlet(null);
    
    // Fetch countries for this business
    await fetchCountries(business.id);
  };

  // Handle country selection
  const handleSelectCountry = async (country) => {
    console.log('[MDM] Country selected:', country.name);
    setSelectedCountry(country);
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedOutlet(null);
    
    // Fetch states for this country
    await fetchStates(country.stateIds || []);
  };

  // Handle state selection
  const handleSelectState = async (state) => {
    console.log('[MDM] State selected:', state.name);
    setSelectedState(state);
    setSelectedCity(null);
    setSelectedOutlet(null);
    
    // Fetch cities for this state
    await fetchCities(state.cityIds || []);
  };

  // Handle city selection
  const handleSelectCity = async (city) => {
    console.log('[MDM] City selected:', city.name);
    setSelectedCity(city);
    setSelectedOutlet(null);
    
    // Fetch outlets for this city
    await fetchOutlets(city.outletIds || []);
  };

  // Handle outlet selection
  const handleSelectOutlet = (outlet) => {
    console.log('[MDM] Outlet selected:', outlet.name);
    setSelectedOutlet(outlet);
    
    // Save to localStorage for persistence
    localStorage.setItem('selectedOutlet', JSON.stringify({
      business: selectedBusiness,
      country: selectedCountry,
      state: selectedState,
      city: selectedCity,
      outlet: outlet
    }));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedBusiness(null);
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedOutlet(null);
    localStorage.removeItem('selectedOutlet');
  };

  // Load saved selection from localStorage
  useEffect(() => {
    const savedSelection = localStorage.getItem('selectedOutlet');
    if (savedSelection) {
      try {
        const { business, country, state, city, outlet } = JSON.parse(savedSelection);
        setSelectedBusiness(business);
        setSelectedCountry(country);
        setSelectedState(state);
        setSelectedCity(city);
        setSelectedOutlet(outlet);
      } catch (err) {
        console.error('[MDM] Error loading saved selection:', err);
        localStorage.removeItem('selectedOutlet');
      }
    }
  }, []);

  // Initial load of businesses
  useEffect(() => {
    if (merchantData) {
      fetchBusinesses();
    }
  }, [merchantData]);

  const value = {
    // Selected items
    selectedBusiness,
    selectedCountry,
    selectedState,
    selectedCity,
    selectedOutlet,
    
    // Lists
    businesses,
    countries,
    states,
    cities,
    outlets,
    
    // State
    loading,
    error,
    
    // Actions
    handleSelectBusiness,
    handleSelectCountry,
    handleSelectState,
    handleSelectCity,
    handleSelectOutlet,
    clearSelection,
    fetchBusinesses,
  };

  return (
    <OutletContext.Provider value={value}>
      {children}
    </OutletContext.Provider>
  );
};
